import { Formik, FormikProps } from 'formik';
import { FV, createInitialValues, defaultInitialValues } from './share';
import Type from './Type';
import Name from './Name';
import TimeRange from './TimeRange';
import PrepareTimeRange from './PrepareTimeRange';
import Stores from './Stores';
import Items from './Items';
import MaxDiscount from './MaxDiscount';
import Highlight from './Highlight';
import { Modal, message } from 'antd';
import {
  createActivity,
  getActivityTypeHighlightListMap,
  updateActivity,
} from '@knowledge/service/api/activity';
import { Store } from 'components/StoreScopes';
import { useTranslate } from '@tolgee/react';
import RecommendItems, { notSupportItemsTypes } from './RecommendItems';
import { ReactNode, useMemo, useState } from 'react';
import { ItemSelectProvider } from './ItemSelectProvider';
import { VoucherConfig } from './VoucherConfig';
import DiscountType from './DiscountType';
import DiscountValue from './DiscountValue';
import MultiPackThreshold from './MultiPackThreshold';
import PromotionType from './PromotionType';
import Slogans from './Slogans';
import { ActivityContextValue } from '../ActivityContextV2';
import { useMount } from 'ahooks';
import Language from './Language';
import TimeRangeScopes from './TimeRangeScopes';
import { LiveCode } from './LiveCode';
import ScenarioConfig from './ScenarioConfig';

export interface ActivityFormProps {
  initialValues?: FV;
  disabled?: boolean;
  storeList?: Store[];
  storeMap?: Partial<Record<number, Store>>;
  listWithExtra?: Knowledge.Activity.WithExtra[];
  activityTypeHighlightListMap?: Partial<
    Record<Knowledge.Activity.Type, Knowledge.Activity.RecommendHighlight[]>
  >;
  onUpdate?: (activity: Knowledge.Activity.Detail) => void;
  children: {
    (props: {
      ready: false;
      type: ReactNode;
      otherFields?: undefined;
      formProps?: undefined;
    }): JSX.Element;
    (props: {
      ready: true;
      type: ReactNode;
      otherFields: ReactNode[];
      formProps: FormikProps<FV>;
    }): JSX.Element;
  };
  onActivityTypeChange?: (activityType: Knowledge.Activity.Type) => void;
}

function ActivityForm(props: ActivityFormProps) {
  const {
    initialValues: initialValuesProp,
    disabled,
    storeList,
    storeMap,
    listWithExtra,
    activityTypeHighlightListMap: activityTypeHighlightListMapProp,
    onUpdate,
    children,
    onActivityTypeChange,
  } = props;

  const { t } = useTranslate();

  const [_activityTypeHighlightListMap, setActivityTypeHighlightListMap] =
    useState<ActivityContextValue['activityTypeHighlightListMap']>();
  const activityTypeHighlightListMap =
    activityTypeHighlightListMapProp || _activityTypeHighlightListMap;

  useMount(() => {
    if (!activityTypeHighlightListMap) {
      getActivityTypeHighlightListMap()
        .then(setActivityTypeHighlightListMap)
        .catch(() => {});
    }
  });

  const initialValues = useMemo(() => {
    // 历史活动初始化
    if (initialValuesProp) {
      const { activityVoucherType } = initialValuesProp;
      return {
        ...initialValuesProp,
        activityVoucherType: activityVoucherType || 'NOT_SEND_VOUCHER_CARD',
      };
    }
    return {
      ...defaultInitialValues,
      activityType: undefined,
    } as unknown as FV;
  }, [initialValuesProp]);

  const isEdit = Boolean(initialValues.id);

  return (
    <Formik
      key={initialValues.id}
      enableReinitialize
      validateOnMount
      initialValues={initialValues}
      onSubmit={(values, helpers) => {
        const { id, ...others } = values;

        return (
          id
            ? updateActivity({ id, ...others }).then(() => ({
                id,
                ...others,
              }))
            : createActivity(others)
        )
          .then((activity) => {
            helpers.resetForm();
            return onUpdate?.(activity);
          })
          .catch(() => {
            message.error(t('operation.saveFailed'));
          });
      }}
    >
      {(formProps) => {
        const {
          values,
          values: { id, activityType, itemsV2, activityVoucherType },
          handleSubmit,
          setValues,
        } = formProps;

        const isSendVoucher = activityVoucherType === 'SEND_VOUCHER_CARD';
        const type = (
          <Type
            disabled={disabled || isEdit}
            type={values.activityType}
            onTypeChange={(type) => {
              const targetActivity = listWithExtra?.find((_) => _.usageStatus === 'USED');
              const { [type]: highlightList = [] } = activityTypeHighlightListMap || {};
              if (itemsV2) {
                setValues({
                  ...values,
                  activityType: type,
                  [notSupportItemsTypes.includes(type) ? 'recommendItems' : 'items']: itemsV2,
                  itemsV2: undefined,
                  activityTheme: targetActivity?.activityTheme,
                  highlightIds: highlightList.slice(0, 3).map(({ id }) => id),
                });
              } else {
                setValues(
                  createInitialValues({
                    activityType: type,
                    activityTheme: targetActivity?.activityTheme,
                    highlightIds: highlightList.slice(0, 3).map(({ id }) => id),
                  }),
                );
              }
              onActivityTypeChange?.(type);
            }}
          />
        );

        if (!activityType) {
          return (
            <ItemSelectProvider>
              {children({
                ready: false,
                type,
              })}
            </ItemSelectProvider>
          );
        }

        const submitWithConfirm = () => {
          const { stores, items } = values;

          if (!items.length) {
            return handleSubmit();
          }

          const noItemStores = stores.filter((store) =>
            items.every((item) => item.storeId !== store.storeId),
          );

          if (!noItemStores.length) {
            return handleSubmit();
          }

          Modal.confirm({
            title: t('activity_submit_item_confirm_title', { x: noItemStores.length }),
            content: t('activity_submit_item_confirm_content'),
            okText: t('activity_submit_item_confirm_button', { x: noItemStores.length }),
            onOk: () => {
              setValues({
                ...values,
                stores: stores.filter((store) =>
                  noItemStores.every((_) => _.storeId !== store.storeId),
                ),
              });
              handleSubmit();
            },
          });
        };

        const name = <Name disabled={disabled} />;
        const timeRange = <TimeRange disabled={disabled} />;
        const timeRangeScopes = activityType === 'LIVE_STREAMING_ACTIVITY' && (
          <TimeRangeScopes disabled={disabled} />
        );
        const prepareTimeRange = ![
          'RECOMMEND_ACTIVITY',
          'NEW_PRODUCT_ONSALE_ACTIVITY',
          'RECOMMEND_HOT_PRODUCT_ACTIVITY',
        ].includes(activityType) && <PrepareTimeRange disabled={disabled} />;
        const stores = (
          <Stores
            key={`stores@${id}@${activityType}`}
            readonly={disabled}
            storeList={storeList}
            storeMap={storeMap}
          />
        );
        const items = !notSupportItemsTypes.includes(activityType) && (
          <Items key={`items@${id}@${activityType}`} disabled={disabled} />
        );
        const recommendItems = activityType !== 'RECOMMEND_ACTIVITY' && (
          <RecommendItems key={`recommendItems@${id}@${activityType}`} disabled={disabled} />
        );
        const multiPackThreshold = activityType === 'MULTI_PACK_DISCOUNT_ACTIVITY' && (
          <MultiPackThreshold disabled={disabled} />
        );
        const voucherConfig = activityType === 'VOUCHER_GIFT_ACTIVITY' && (
          <VoucherConfig
            key={`voucherConfig@${id}@${activityType}`}
            disabled={disabled}
            isEdit={isEdit}
          />
        );
        const discountType = !isSendVoucher &&
          (activityType === 'VOUCHER_GIFT_ACTIVITY' ||
            activityType === 'MULTI_PACK_DISCOUNT_ACTIVITY') && (
            <DiscountType disabled={disabled} />
          );
        const discountValue = !isSendVoucher &&
          (activityType === 'VOUCHER_GIFT_ACTIVITY' ||
            activityType === 'MULTI_PACK_DISCOUNT_ACTIVITY') && (
            <DiscountValue disabled={disabled} />
          );
        const maxDiscount = (activityType === 'DISCOUNT_ACTIVITY' ||
          activityType === 'FLASH_ACTIVITY') && <MaxDiscount disabled={disabled} />;
        const slogans = <Slogans disabled={disabled} />;
        const highlight = <Highlight key={activityType} disabled={disabled} />;
        const promotionType = [
          'NEW_PRODUCT_ONSALE_ACTIVITY',
          'CLEARANCE_SALE_ACTIVITY',
          'RECOMMEND_HOT_PRODUCT_ACTIVITY',
          'LIVE_STREAMING_ACTIVITY',
        ].includes(activityType) && <PromotionType disabled={disabled} isEdit={isEdit} />;
        const liveCode = activityType === 'LIVE_STREAMING_ACTIVITY' && (
          <LiveCode disabled={disabled} />
        );
        const language = <Language disabled={disabled} />;
        const scenarioConfig = <ScenarioConfig disabled={disabled} />;

        const otherFields = [
          name,
          timeRange,
          timeRangeScopes,
          prepareTimeRange,
          stores,
          items,
          items && slogans,
          recommendItems,
          !items && slogans,
          multiPackThreshold,
          voucherConfig,
          discountType,
          discountValue,
          maxDiscount,
          liveCode,
          highlight,
          promotionType,
          language,
          scenarioConfig,
        ];

        return (
          <ItemSelectProvider>
            {children({
              ready: true,
              type,
              otherFields,
              formProps: { ...formProps, handleSubmit: submitWithConfirm },
            })}
          </ItemSelectProvider>
        );
      }}
    </Formik>
  );
}

export default ActivityForm;
